/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type FC } from 'react';

import { css, cssMap, jsx } from '@compiled/react';
import { FormattedMessage } from 'react-intl';

import type { CustomGlyphProps, Size } from '@atlaskit/icon/types';
import { AtlassianIcon } from '@atlaskit/logo/atlassian-icon';
import type { LogoProps } from '@atlaskit/logo/types';
import {
	BitbucketIcon,
	CompassIcon,
	ConfluenceIcon,
	JiraIcon,
	JiraProductDiscoveryIcon,
	JiraServiceManagementIcon,
	TrelloIcon,
} from '@atlaskit/logo';

// This is the only place we want to import this
import { DO_NOT_IMPORT_ProductKeys3P, DO_NOT_IMPORT_ProductsOrder3P } from './3p-product-configs';
import { AirtableIcon } from './icons/airtable';
import { AirtableFilterIcon } from './icons/airtable-filter';
import { AsanaIcon } from './icons/asana';
import { AtlasSearchResultIcon } from './icons/atlas-search';
import { ConfluenceSearchResultIcon } from './icons/confluence-search';
import { DropboxIcon } from './icons/dropbox';
import { FigmaIcon } from './icons/figma';
import { FigmaSearchResultIcon } from './icons/figma-search';
import { GithubIcon } from './icons/github';
import { GithubSearchResultIcon } from './icons/github-search';
import { GmailIcon } from './icons/gmail';
import { GoogleCalendarIcon } from './icons/google-calendar';
import { GoogleDriveIcon } from './icons/google-drive';
import { GoogleDriveSearchResultIcon } from './icons/google-drive-search';
import { JiraSearchResultIcon } from './icons/jira-search';
import { LoomIcon } from './icons/loom';
import { MiroIcon } from './icons/miro';
import { MiroFilterIcon } from './icons/miro-filter';
import { NotionIcon } from './icons/notion';
import { NotionFilterIcon } from './icons/notion-filter';
import { OneDriveIcon } from './icons/onedrive';
import { OutlookCalendarIcon } from './icons/outlook-calendar';
import { SharePointIcon } from './icons/sharepoint';
import { SharePointSearchResultIcon } from './icons/sharepoint-search';
import { SlackIcon } from './icons/slack';
import { SlackSearchResultIcon } from './icons/slack-search';
import { TeamsIcon } from './icons/teams';
import { TeamsSearchResultIcon } from './icons/teams-search';
import { WebDomainIcon } from './icons/web-domain';
import { WebDomainSearchResultIcon } from './icons/web-domain-search';
import { ZendeskIcon } from './icons/zendesk';
import { messages } from './messages';

const iconStyles = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
});

const iconSizeStyles = cssMap({
	small: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& > svg': {
			width: '16px',
			height: '16px',
		},
	},
	medium: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& > svg': {
			width: '24px',
			height: '24px',
		},
	},
	large: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& > svg': {
			width: '32px',
			height: '32px',
		},
	},
});

type WithRequiredProperty<Type, Key extends keyof Type> = Type & {
	[Property in Key]-?: Type[Property];
};

export type IconProps = Omit<
	WithRequiredProperty<LogoProps, 'appearance'>,
	'iconColor' | 'textColor' | 'size'
> & {
	size?: SmallOrMedium;
};

export interface ProductConfig {
	displayName: React.ReactNode;
	// eslint-disable-next-line @typescript-eslint/no-restricted-types
	Icon: FC<IconProps>;
	is3P?: boolean;
	// eslint-disable-next-line @typescript-eslint/no-restricted-types
	SearchResultIcon?: FC<IconProps>;
}

const ProductKeys1P = {
	Atlas: 'townsquare',
	Bitbucket: 'bitbucket',
	Compass: 'compass',
	Confluence: 'confluence',
	Jira: 'jira',
	JiraProductDiscovery: 'jira-product-discovery',
	JiraServiceManagement: 'jira-servicedesk',
	Opsgenie: 'opsgenie',
	Trello: 'trello',
} as const;

export const ProductKeys3P: {
	readonly Airtable: 'airtable';
	readonly Asana: 'asana';
	readonly AtlassianWebCrawler: 'atlassian-web-crawler';
	readonly Dropbox: 'dropbox';
	readonly Figma: 'figma';
	readonly Github: 'github';
	readonly Gmail: 'gmail';
	readonly GoogleCalendar: 'google-calendar';
	readonly GoogleDrive: 'drive';
	readonly Loom: 'loom';
	readonly Miro: 'miro';
	readonly Notion: 'notion';
	readonly OneDrive: 'onedrive';
	readonly OutlookCalendar: 'outlook-calendar';
	readonly Sharepoint: 'sharepoint';
	readonly Slack: 'slack';
	readonly Teams: 'teams';
	readonly Zendesk: 'zendesk';
} = DO_NOT_IMPORT_ProductKeys3P;

export const ProductKeys: {
	readonly Airtable: 'airtable';
	readonly Asana: 'asana';
	readonly Atlas: 'townsquare';
	readonly AtlassianWebCrawler: 'atlassian-web-crawler';
	readonly Bitbucket: 'bitbucket';
	readonly Compass: 'compass';
	readonly Confluence: 'confluence';
	readonly Dropbox: 'dropbox';
	readonly Figma: 'figma';
	readonly Github: 'github';
	readonly Gmail: 'gmail';
	readonly GoogleCalendar: 'google-calendar';
	readonly GoogleDrive: 'drive';
	readonly Jira: 'jira';
	readonly JiraProductDiscovery: 'jira-product-discovery';
	readonly JiraServiceManagement: 'jira-servicedesk';
	readonly Loom: 'loom';
	readonly Miro: 'miro';
	readonly Notion: 'notion';
	readonly OneDrive: 'onedrive';
	readonly Opsgenie: 'opsgenie';
	readonly OutlookCalendar: 'outlook-calendar';
	readonly Sharepoint: 'sharepoint';
	readonly Slack: 'slack';
	readonly Teams: 'teams';
	readonly Trello: 'trello';
	readonly Zendesk: 'zendesk';
} = {
	...ProductKeys1P,
	...DO_NOT_IMPORT_ProductKeys3P,
} as const;

export type ProductKeys3P =
	(typeof DO_NOT_IMPORT_ProductKeys3P)[keyof typeof DO_NOT_IMPORT_ProductKeys3P];
export type ProductKeys1P = (typeof ProductKeys1P)[keyof typeof ProductKeys1P];
export type ProductKeys = (typeof ProductKeys)[keyof typeof ProductKeys];
export const allProductKeys: ProductKeys[] = Object.values(ProductKeys);
export const all3pProductKeys: ProductKeys3P[] = Object.values(DO_NOT_IMPORT_ProductKeys3P);
export const all1pProductKeys: ProductKeys1P[] = Object.values(ProductKeys1P);
export const is3pProductKey = (key: string): key is ProductKeys3P =>
	all3pProductKeys.includes(key as ProductKeys3P);

// This is different to ProductKeys, it represents products we have support for mounting Search Page in
export const PrimaryProductKeys = {
	// Fully supported as GA
	Atlas: 'townsquare',
	Confluence: 'confluence',
	// In development
	Jira: 'jira',
	Mercury: 'mercury',
	Elevate: 'elevate',
} as const;
export type PrimaryProductKey = (typeof PrimaryProductKeys)[keyof typeof PrimaryProductKeys];

/**
 * Due a bug in ADS custom icons are one size smaller
 * To accommodate for this behavior you need to use one size bigger than what you need
 * See: https://atlassian.slack.com/archives/CFJ9DU39U/p1714096323217799
 */
type SmallOrMedium = 'xxsmall' | Exclude<Size, 'large' | 'xlarge'>;
type MediumOrLarge = Exclude<Size, 'xlarge'>;
const sizesCorrection: Record<SmallOrMedium, MediumOrLarge> = {
	xxsmall: 'small',
	small: 'medium',
	medium: 'large',
};

export const getIcon = ({
	label,
	icon,
}: {
	icon: (glyphProps: CustomGlyphProps) => JSX.Element;
	label: string;
}) => {
	return ({ size = 'medium' }: { size?: SmallOrMedium }): JSX.Element => (
		<span css={[iconStyles, iconSizeStyles[sizesCorrection[size]]]}>
			{icon({ 'aria-label': label })}
		</span>
	);
};

/**
 * This is the source of truth for the products that are supported in Full Page Search.
 * Note: there is additional logic in controllers/store/bootstrap/utils.ts for products being rolled out.
 *
 * QS-5444 3P references in this object are being deprecated. Please add to ./3p-product-configs
 */
export const products: Record<string, ProductConfig> = {
	[ProductKeys.Confluence]: {
		displayName: <FormattedMessage {...messages.confluence} />,
		Icon: ConfluenceIcon,
		SearchResultIcon: getIcon({
			icon: ConfluenceSearchResultIcon,
			label: messages.confluence.defaultMessage,
		}),
	},
	[ProductKeys.Atlas]: {
		displayName: <FormattedMessage {...messages.atlas} />,
		Icon: AtlassianIcon,
		SearchResultIcon: getIcon({
			icon: AtlasSearchResultIcon,
			label: messages.atlas.defaultMessage,
		}),
	},
	[ProductKeys.Jira]: {
		displayName: <FormattedMessage {...messages.jira} />,
		Icon: JiraIcon,
		SearchResultIcon: getIcon({
			icon: JiraSearchResultIcon,
			label: messages.jira.defaultMessage,
		}),
	},
	[ProductKeys.JiraProductDiscovery]: {
		displayName: <FormattedMessage {...messages.jiraProductDiscovery} />,
		Icon: JiraProductDiscoveryIcon,
	},
	[ProductKeys.JiraServiceManagement]: {
		displayName: <FormattedMessage {...messages.jiraServiceManagement} />,
		Icon: JiraServiceManagementIcon,
	},
	[ProductKeys.Bitbucket]: {
		displayName: <FormattedMessage {...messages.bitbucket} />,
		Icon: BitbucketIcon,
		SearchResultIcon: BitbucketIcon,
	},
	[ProductKeys.Trello]: {
		displayName: <FormattedMessage {...messages.trello} />,
		Icon: TrelloIcon,
	},
	// [ProductKeys.Opsgenie]: {
	// 	displayName: 'Opsgenie',
	// 	Icon: OpsgenieIcon,
	// },
	[ProductKeys.Compass]: {
		displayName: <FormattedMessage {...messages.compass} />,
		Icon: CompassIcon,
	},
	[ProductKeys.GoogleDrive]: {
		displayName: <FormattedMessage {...messages.googleDrive} />,
		Icon: getIcon({
			icon: GoogleDriveIcon,
			label: messages.googleDrive.defaultMessage,
		}),
		is3P: true,
		SearchResultIcon: getIcon({
			icon: GoogleDriveSearchResultIcon,
			label: messages.googleDrive.defaultMessage,
		}),
	},
	[ProductKeys.Sharepoint]: {
		displayName: <FormattedMessage {...messages.microsoftSharepoint} />,
		Icon: getIcon({ icon: SharePointIcon, label: messages.sharepoint.defaultMessage }),
		is3P: true,
		SearchResultIcon: getIcon({
			icon: SharePointSearchResultIcon,
			label: messages.sharepoint.defaultMessage,
		}),
	},
	[ProductKeys.AtlassianWebCrawler]: {
		displayName: <FormattedMessage {...messages.webPage} />,
		Icon: getIcon({ icon: WebDomainIcon, label: messages.atlassianWebCrawler.defaultMessage }),
		is3P: true,
		SearchResultIcon: getIcon({
			icon: WebDomainSearchResultIcon,
			label: messages.atlassianWebCrawler.defaultMessage,
		}),
	},
	[ProductKeys.Teams]: {
		displayName: <FormattedMessage {...messages.teams} />,
		Icon: getIcon({ icon: TeamsIcon, label: messages.teams.defaultMessage }),
		is3P: true,
		SearchResultIcon: getIcon({
			icon: TeamsSearchResultIcon,
			label: messages.teams.defaultMessage,
		}),
	},
	[ProductKeys.Loom]: {
		displayName: <FormattedMessage {...messages.loom} />,
		Icon: getIcon({ icon: LoomIcon, label: messages.loom.defaultMessage }),
		is3P: true,
	},
	[ProductKeys.OneDrive]: {
		displayName: <FormattedMessage {...messages.oneDrive} />,
		Icon: getIcon({ icon: OneDriveIcon, label: messages.oneDrive.defaultMessage }),
		is3P: true,
	},
	[ProductKeys.Slack]: {
		displayName: <FormattedMessage {...messages.slack} />,
		Icon: getIcon({ icon: SlackIcon, label: messages.slack.defaultMessage }),
		is3P: true,
		SearchResultIcon: getIcon({
			icon: SlackSearchResultIcon,
			label: messages.slack.defaultMessage,
		}),
	},
	[ProductKeys.Github]: {
		displayName: <FormattedMessage {...messages.github} />,
		Icon: getIcon({ icon: GithubIcon, label: messages.github.defaultMessage }),
		is3P: true,
		SearchResultIcon: getIcon({
			icon: GithubSearchResultIcon,
			label: messages.github.defaultMessage,
		}),
	},
	[ProductKeys.Figma]: {
		displayName: <FormattedMessage {...messages.figma} />,
		Icon: getIcon({ icon: FigmaIcon, label: messages.figma.defaultMessage }),
		is3P: true,
		SearchResultIcon: getIcon({
			icon: FigmaSearchResultIcon,
			label: messages.figma.defaultMessage,
		}),
	},
	[ProductKeys.Dropbox]: {
		displayName: <FormattedMessage {...messages.dropbox} />,
		Icon: getIcon({ icon: DropboxIcon, label: messages.dropbox.defaultMessage }),
		is3P: true,
	},
	[ProductKeys.GoogleCalendar]: {
		displayName: <FormattedMessage {...messages.googleCalendar} />,
		Icon: getIcon({
			icon: GoogleCalendarIcon,
			label: messages.googleCalendar.defaultMessage,
		}),
		is3P: true,
	},
	[ProductKeys.Gmail]: {
		displayName: <FormattedMessage {...messages.gmail} />,
		Icon: getIcon({ icon: GmailIcon, label: messages.gmail.defaultMessage }),
		is3P: true,
	},
	[ProductKeys.OutlookCalendar]: {
		displayName: <FormattedMessage {...messages.outlookCalendar} />,
		Icon: getIcon({
			icon: OutlookCalendarIcon,
			label: messages.outlookCalendar.defaultMessage,
		}),
		is3P: true,
	},
	[ProductKeys.Airtable]: {
		displayName: <FormattedMessage {...messages.airtable} />,
		Icon: getIcon({
			icon: AirtableFilterIcon,
			label: messages.airtable.defaultMessage,
		}),
		is3P: true,
		SearchResultIcon: getIcon({
			icon: AirtableIcon,
			label: messages.airtable.defaultMessage,
		}),
	},
	[ProductKeys.Asana]: {
		displayName: <FormattedMessage {...messages.asana} />,
		Icon: getIcon({ icon: AsanaIcon, label: messages.asana.defaultMessage }),
		is3P: true,
	},
	[ProductKeys.Zendesk]: {
		displayName: <FormattedMessage {...messages.zendesk} />,
		Icon: getIcon({ icon: ZendeskIcon, label: messages.zendesk.defaultMessage }),
		is3P: true,
	},
	[ProductKeys.Notion]: {
		displayName: <FormattedMessage {...messages.notion} />,
		Icon: getIcon({
			icon: NotionFilterIcon,
			label: messages.notion.defaultMessage,
		}),
		is3P: true,
		SearchResultIcon: getIcon({
			icon: NotionIcon,
			label: messages.notion.defaultMessage,
		}),
	},
	[ProductKeys.Miro]: {
		displayName: <FormattedMessage {...messages.miro} />,
		Icon: getIcon({ icon: MiroFilterIcon, label: messages.miro.defaultMessage }),
		is3P: true,
		SearchResultIcon: getIcon({
			icon: MiroIcon,
			label: messages.miro.defaultMessage,
		}),
	},
};

export const PRODUCTS_ORDER: ProductKeys[] = [
	ProductKeys.Confluence,
	ProductKeys.Jira,
	ProductKeys.JiraProductDiscovery,
	ProductKeys.JiraServiceManagement,
	ProductKeys.Atlas,
	ProductKeys.Trello,
	ProductKeys.Bitbucket,
	ProductKeys.Opsgenie,
	ProductKeys.Compass,

	...DO_NOT_IMPORT_ProductsOrder3P,
];
